import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Network, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

const INITIAL_PAGES_PER_CAT = 8;
const COLLAPSE_TOTAL_THRESHOLD = 100;
const COLLAPSE_PER_CAT_THRESHOLD = 20;

type IndexEntry = { slug: string; title: string; takeaway: string; updatedAt: string };

function matches(entry: IndexEntry, term: string): boolean {
  if (!term) return true;
  const t = term.toLowerCase();
  return (
    entry.slug.toLowerCase().includes(t) ||
    entry.title.toLowerCase().includes(t) ||
    entry.takeaway.toLowerCase().includes(t)
  );
}

export function WikiIndex() {
  const indexQuery = useQuery({
    queryKey: ['wiki', 'index'],
    queryFn: api.wikiIndex,
    staleTime: 30_000,
  });

  const [search, setSearch] = useState('');
  const [collapsedOverride, setCollapsedOverride] = useState<Record<string, boolean>>({});

  const isLargeWiki = useMemo(
    () => (indexQuery.data?.totalPages ?? 0) >= COLLAPSE_TOTAL_THRESHOLD,
    [indexQuery.data?.totalPages],
  );

  if (indexQuery.isLoading) {
    return <div className="p-8 text-foreground-muted text-sm">로딩 중…</div>;
  }
  if (indexQuery.isError) {
    return (
      <div className="p-8 text-destructive text-sm">
        목차를 불러오지 못했습니다: {(indexQuery.error as Error).message}
      </div>
    );
  }

  const index = indexQuery.data;
  if (!index) return null;
  const term = search.trim();

  function isCollapsed(catId: string, pageCount: number, hasMatch: boolean): boolean {
    if (term && hasMatch) return false; // 검색 시 매칭된 카테고리 강제 펼침
    if (catId in collapsedOverride) return collapsedOverride[catId] ?? false;
    return isLargeWiki || pageCount > COLLAPSE_PER_CAT_THRESHOLD;
  }

  function toggleCollapse(catId: string, currentlyCollapsed: boolean) {
    setCollapsedOverride((prev) => ({ ...prev, [catId]: !currentlyCollapsed }));
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">지식 위키</h1>
        <p className="mt-2 text-foreground-muted">
          전체 페이지 {index.totalPages.toLocaleString()}개. 카테고리별로 정리되어 있습니다.
        </p>
      </header>

      <div className="mb-6 flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2">
        <Search className="size-4 text-foreground-muted" />
        <input
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder="제목 / 슬러그 / takeaway 부분 일치 검색"
          className="flex-1 bg-transparent text-sm focus:outline-none"
        />
      </div>

      {isLargeWiki ? (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-accent/30 bg-accent/5 px-4 py-3 text-sm">
          <Network className="mt-0.5 size-4 text-accent" />
          <p className="flex-1 text-foreground-muted">
            페이지가 많습니다 ({index.totalPages}개). 구조를 한눈에 보려면{' '}
            <Link to="/wikis/graph" className="font-medium text-accent underline">
              지식 그래프
            </Link>{' '}
            또는{' '}
            <Link to="/wikis/search" className="font-medium text-accent underline">
              전체 검색
            </Link>
            을 활용하세요.
          </p>
        </div>
      ) : null}

      {index.categories.length === 0 && index.uncategorized.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-8 text-center text-foreground-muted">
          <p className="mb-2 text-base">아직 작성된 위키 페이지가 없습니다.</p>
          <p className="text-sm">
            <Link to="/wikis/upload" className="text-accent underline">
              자료 올리기
            </Link>
            에서 PDF/HWP/Word/Excel 등을 업로드하면 LLM 이 자동으로 페이지를 만듭니다.
          </p>
        </div>
      ) : null}

      <div className="space-y-4">
        {index.categories.map((cat) => {
          const filtered = cat.pages.filter((p) => matches(p, term));
          const hasMatch = filtered.length > 0;
          const collapsed = isCollapsed(cat.category.id, cat.pages.length, hasMatch);
          const visiblePages = collapsed ? [] : filtered.slice(0, INITIAL_PAGES_PER_CAT);
          const moreCount = collapsed ? 0 : Math.max(0, filtered.length - INITIAL_PAGES_PER_CAT);

          // 검색 중인데 매치 0개면 카테고리 자체 숨김
          if (term && !hasMatch) return null;

          return (
            <section key={cat.category.id} className="rounded-md border border-border bg-surface">
              <button
                type="button"
                onClick={() => toggleCollapse(cat.category.id, collapsed)}
                className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-background"
              >
                {collapsed ? (
                  <ChevronRight className="size-4 text-foreground-muted" />
                ) : (
                  <ChevronDown className="size-4 text-foreground-muted" />
                )}
                <span
                  className="inline-block size-3 rounded-full shrink-0"
                  style={{ backgroundColor: cat.category.color }}
                />
                <Link
                  to="/wikis/c/$slug"
                  params={{ slug: cat.category.slug }}
                  className="text-base font-semibold hover:text-accent"
                  onClick={(e) => e.stopPropagation()}
                >
                  {cat.category.name}
                </Link>
                <span className="text-sm text-foreground-muted">({cat.pages.length})</span>
                {cat.category.description ? (
                  <span className="ml-2 truncate text-xs text-foreground-faint">
                    {cat.category.description}
                  </span>
                ) : null}
              </button>

              {!collapsed ? (
                <div className="border-t border-border px-2 pb-3 pt-1">
                  {visiblePages.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-foreground-muted">
                      아직 페이지가 없습니다.
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {visiblePages.map((p) => (
                        <li key={p.slug}>
                          <Link
                            to="/wikis/p/$slug"
                            params={{ slug: p.slug }}
                            className="block rounded-md px-3 py-2 hover:bg-background"
                          >
                            <div className="font-medium text-foreground">{p.title}</div>
                            <div className="mt-0.5 text-sm text-foreground-muted line-clamp-2">
                              {p.takeaway}
                            </div>
                            <code className="mt-0.5 inline-block rounded bg-code-bg px-1 py-0.5 text-xs text-foreground-faint">
                              {p.slug}
                            </code>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {moreCount > 0 ? (
                    <Link
                      to="/wikis/c/$slug"
                      params={{ slug: cat.category.slug }}
                      className="mt-2 block px-3 py-2 text-sm text-accent hover:underline"
                    >
                      + {moreCount}개 더 보기 →
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </section>
          );
        })}

        {index.uncategorized.length > 0
          ? (() => {
              const filtered = index.uncategorized.filter((p) => matches(p, term));
              if (term && filtered.length === 0) return null;
              return (
                <section className="rounded-md border border-border bg-surface px-4 py-3">
                  <h2 className="mb-2 text-base font-semibold">
                    분류되지 않음
                    <span className="ml-2 text-sm font-normal text-foreground-muted">
                      ({index.uncategorized.length})
                    </span>
                  </h2>
                  <ul className="space-y-1">
                    {filtered.slice(0, INITIAL_PAGES_PER_CAT).map((p) => (
                      <li key={p.slug}>
                        <Link
                          to="/wikis/p/$slug"
                          params={{ slug: p.slug }}
                          className="block rounded-md px-3 py-2 hover:bg-background"
                        >
                          <div className="font-medium">{p.title}</div>
                          <div className="mt-0.5 text-sm text-foreground-muted line-clamp-2">
                            {p.takeaway}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {filtered.length > INITIAL_PAGES_PER_CAT ? (
                    <p className="px-3 pt-1 text-sm text-foreground-faint">
                      + {filtered.length - INITIAL_PAGES_PER_CAT}개 더 (검색으로 좁혀보세요)
                    </p>
                  ) : null}
                </section>
              );
            })()
          : null}
      </div>
    </div>
  );
}
